import React, { useCallback, useState, useEffect } from 'react';
import { Paper, Table, TableRow, TableCell, TableHead, TableBody, makeStyles, CircularProgress, Checkbox, Button, Box, Theme }
    from '@material-ui/core';
import clsx from 'clsx';
import { BaseListCompField, BaseListCompFieldsSettings, BaseListCompFieldsSet, createHeaderColumn, BaseListCompProps, useModeTableStyle,
    useModePanelStyle, useModeFormStyle, FontIcon, BaseInfoCompProps, BaseInfoCompActionsSet, BaseInfoCompSaveAction, BaseListCompDataItem,
    GenericStatusIndicator, InfoCompPanelHeader } from '@moderepo/mode-app-components';
import { SCSensorInfo } from '../../containers/sensor-cloud/SCUtils';



const useStyle = makeStyles((theme: Theme) => {
    return {

        panelContent: {
            padding      : 0,
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        tableContainer: {
            overflow: 'auto',
            width   : '100%',
        },

        tableCol: {
            '&.name-col': {
                textAlign    : 'left',
                whiteSpace   : 'nowrap',
                '&.fullwidth': {
                    width: '100%',
                },
            },
            '&.display-name-col': {
                textAlign : 'left',
                width     : '100%',
                whiteSpace: 'nowrap',
            },
            '&.enabled-col': {
                textAlign: 'center',
            },
        },

        formActionContainer: {
            padding: theme.spacing(2),
        },

    };
}, { name: 'SensorsTable' });



/**
 * This is the data structure for the row data to be displayed in the table. The value for all these fields will be strings because
 * they will be displayed in the table. These values MUST be localized/formatted/processed by the container. The table is a dumb
 * component and does not know what these values mean so it will only display these values and nothing else.
 * For each column this table has, there should be a field in this data structure corresponding to that column.
 */
export interface SCDisplayableSensorDataItem {
    readonly name: string;
    readonly displayName: string;
    readonly enabled: string;
}


/**
 * Each row of table data will have 2 data, the display data (all strings) used for displaying in the table cell. The actual data used for adding
 * special UI for the data. Actual data is not used often but we will need it in some cases. For example, sometime the data contain some status
 * such as connected/disconnected, verified/not verified, etc... For these cases, we don't just want to show plain text but we also want to add
 * some color to highlight these statuses. For example, we want to show green background for connected or verified status and red background for
 * disconnected or not verified statuses. Therefore, this is the reason we need both displayed data and actual data for all table data.
 */
export interface SCSensorTableDataItem extends BaseListCompDataItem<SCSensorInfo> {
    readonly displayValue: SCDisplayableSensorDataItem;
}


/**
 * Table columns setting for Sensors table. Sensors table columns settings MUST contain these column keys
 */
interface SCSensorsTableFieldsSet extends BaseListCompFieldsSet {
    readonly name: BaseListCompField;
    readonly displayName: BaseListCompField;
    readonly enabled: BaseListCompField;
}


/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface SCSensorsTableUpdatableInputs {
    readonly sensorsStatus: {[sensorValueName: string]: boolean};
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface RCSensorsTableInfoActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<SCSensorsTableUpdatableInputs>
}


export interface SCSensorsTableInfoProps extends BaseInfoCompProps {
    readonly actions?: RCSensorsTableInfoActionsSet | undefined;
}



export interface SCSensorsTableProps extends BaseListCompProps<SCSensorInfo>, SCSensorsTableInfoProps {
    readonly listData: readonly SCSensorTableDataItem[] | undefined;
    readonly fieldsSettings: BaseListCompFieldsSettings<SCSensorsTableFieldsSet>;
}



/**
 * This is a DUMB component that is in used for displaying the list of sensor module's sensors. All this component does is display data
 * provided through props. This component does not have any logic and any user action will be dispatched to the container's handlers.
 */
export const SCSensorsTable: React.FC<SCSensorsTableProps> = (props: SCSensorsTableProps) => {

    const [enabledSensors, setEnabledSensors] = useState<{[sensorValueName: string]: boolean}>({});

    // We need to initialize the checkboxes with the "enabled" state of the props.sensors. So we need to wait
    // for props.sensors to be set before we can initialize the checked state
    useEffect(() => {
        if (props.listData) {
            setEnabledSensors(props.listData.reduce((result: {[sensorValueName: string]: boolean}, sensor: SCSensorTableDataItem) => {
                return {
                    ...result,
                    [sensor.actualValue.name]: sensor.actualValue.enabled,
                };
            }, {}));
        }
    }, [props.listData]);


    const tableClasses = useModeTableStyle();
    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();



    /**
     * On form submit, return a map of all the sensors and their "checked" statuses.
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.listData && props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                sensorsStatus: props.listData.reduce((result: {[sensorValueName: string]: boolean}, sensor: SCSensorTableDataItem) => {
                    const sensorName = sensor.actualValue.name;
                    return {
                        ...result,
                        [sensorName]: enabledSensors[sensorName] !== undefined ? enabledSensors[sensorName]
                            : sensor.actualValue.enabled,
                    };
                }, {}),
            });
        }

        return false;
    }, [props, enabledSensors]);


    const onSensorSelected = useCallback((sensorValueName: string, value: boolean) => {
        setEnabledSensors({
            ...enabledSensors,
            [sensorValueName]: value,
        });
    }, [enabledSensors]);


    return (
        <Paper elevation={2} className={clsx(panelClasses.root, props.className)}>
            <InfoCompPanelHeader {...props} />

            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>

                {props.listData && props.listData.length > 0 && (
                    <form
                        className={clsx(formClasses.root, props.formProps?.className)}
                        noValidate
                        autoComplete="on"
                        method="POST"
                        onSubmit={onFormSubmit}
                    >
                        <div className={clsx(formClasses.formInputGroup, classes.tableContainer)}>
                            <Table className={tableClasses.root} size="medium">
                                <TableHead className={tableClasses.tableHeader}>
                                    <TableRow className={tableClasses.tableHeaderRow}>
                                        {createHeaderColumn(
                                            props.fieldsSettings, 'name',
                                            props.fieldsSettings.fields.name, `${tableClasses.tableCol} ${classes.tableCol} name-col`,
                                        )}
                                        {createHeaderColumn(
                                            props.fieldsSettings, 'displayName',
                                            props.fieldsSettings.fields.displayName, `${tableClasses.tableCol} ${classes.tableCol} display-name-col`,
                                        )}
                                        {createHeaderColumn(
                                            props.fieldsSettings, 'enabled',
                                            props.fieldsSettings.fields.enabled, `${tableClasses.tableCol} ${classes.tableCol} enabled-col`,
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <>
                                        {props.listData.map((sensor: SCSensorTableDataItem): JSX.Element => {
                                            return (
                                                <TableRow
                                                    className={clsx(tableClasses.tableBodyRow)}
                                                    key={sensor.actualValue.name}
                                                >
                                                    {!props.fieldsSettings.fields.name.hidden && (
                                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'name-col',
                                                            props.fieldsSettings.fields.displayName.hidden && 'fullwidth')}
                                                        >
                                                            {sensor.displayValue.name}
                                                        </TableCell>
                                                    )}

                                                    {!props.fieldsSettings.fields.displayName.hidden && (
                                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'display-name-col')}>
                                                            {sensor.displayValue.displayName}
                                                        </TableCell>
                                                    )}

                                                    {!props.fieldsSettings.fields.enabled.hidden && (
                                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'enabled-col')}>
                                                            {!props.isEditing && (
                                                                <GenericStatusIndicator
                                                                    status={Boolean(sensor.actualValue.enabled)}
                                                                />

                                                            )}

                                                            {props.isEditing && (
                                                                <Checkbox
                                                                    key={sensor.actualValue.name}
                                                                    checked={enabledSensors[sensor.actualValue.name]}
                                                                    color="primary"
                                                                    onChange={(event: React.ChangeEvent) => {
                                                                        onSensorSelected(
                                                                            sensor.actualValue.name, (event.target as HTMLInputElement).checked,
                                                                        );
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                    )}

                                                </TableRow>
                                            );
                                        })}
                                    </>
                                </TableBody>
                            </Table>
                        </div>
                        {props.isEditing && props.actions && (
                            <div className={clsx(formClasses.formActionContainer, classes.formActionContainer)}>
                                <div />
                                <div>
                                    {props.actions?.cancelEdit && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            size="small"
                                            startIcon={props.actions.cancelEdit.icon
                                                ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                            onClick={() => {
                                                if (props.actions?.cancelEdit) {
                                                    props.actions.cancelEdit.onClick();
                                                }
                                            }}
                                        >
                                            {props.actions.cancelEdit.label}
                                        </Button>
                                    )}
                                    {props.actions?.saveUpdate && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            size="small"
                                            startIcon={props.actions.saveUpdate.icon
                                                ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                        >
                                            {props.actions.saveUpdate.label}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                )}

                {props.listData && props.listData.length === 0 && props.emptyDataMessageComp}

                {!props.listData && (
                    <Box padding={2}>
                        <CircularProgress />
                    </Box>
                )}
            </div>
        </Paper>
    );
};
